import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Fab,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  CircularProgress,
  Button,
  Paper,
  Tooltip,
  makeStyles,
  FormControlLabel,
  Checkbox
} from "@material-ui/core";
import { Close as CloseIcon, History as HistoryIcon } from "@material-ui/icons";
import api from "api";
import "./AIAssistantFab.css";

const useStyles = makeStyles(theme => ({
  sendButton: {
    minWidth: 48,
    marginLeft: 8
  }
}));

const getInitialPosition = () => {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const boxW = 420;
  const boxH = Math.min(640, h * 0.75);
  return {
    x: Math.max(20, (w - boxW) / 2),
    y: Math.max(20, (h - boxH) / 2)
  };
};

/**
 * Botão flutuante + popup do assistente de IA para análise de projeto (galeria, etc.).
 * Reutilizável em qualquer view que forneça folderPath e token.
 *
 * @param {string} folderPath - Caminho do projeto no blob (ex: projetos/fotos/nome_projeto/2025-01-01)
 * @param {string} token - Token de autenticação
 * @param {boolean} visible - Se false, não renderiza o botão (ex: galeria pública)
 */
const AIAssistantFab = ({ folderPath, token, visible = true }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [userMessage, setUserMessage] = useState("");
  const [greeting, setGreeting] = useState("");
  const [greetingLoading, setGreetingLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [streamingText, setStreamingText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [showConversations, setShowConversations] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [position, setPosition] = useState(getInitialPosition);
  const [totalTokensUsed, setTotalTokensUsed] = useState(null);
  const [progressPhoto, setProgressPhoto] = useState(null);
  const [thinkingText, setThinkingText] = useState("Pensando");
  const [contextFiles, setContextFiles] = useState([]);
  const [planInfo, setPlanInfo] = useState(null);
  const [planTokenLimit, setPlanTokenLimit] = useState(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [annotateImages, setAnnotateImages] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(null);
  const [savingResumo, setSavingResumo] = useState(false);
  const dragRef = useRef({ offsetX: 0, offsetY: 0 });
  const contentEndRef = useRef(null);
  const lastAspectoRef = useRef("");
  const navigate = useNavigate();

  const handleOpen = useCallback(() => {
    setPosition(getInitialPosition());
    setOpen(true);
  }, []);

  const handleDragStart = useCallback(e => {
    if (e.button !== 0) return;
    dragRef.current = {
      offsetX: e.clientX - position.x,
      offsetY: e.clientY - position.y
    };
    const onMove = ev => {
      setPosition(prev => ({
        x: Math.max(0, ev.clientX - dragRef.current.offsetX),
        y: Math.max(0, ev.clientY - dragRef.current.offsetY)
      }));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [position.x, position.y]);

  const scrollToBottom = () => {
    if (contentEndRef.current) {
      contentEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingText]);

  const dots = [".", "..", "..."];
  useEffect(() => {
    if (!sending || streamingText || (progressPhoto && progressPhoto.total > 0)) return;
    let n = 0;
    const id = setInterval(() => {
      n = (n + 1) % (dots.length + 1);
      setThinkingText(n === 0 ? "Pensando" : `Pensando${dots[n - 1]}`);
    }, 400);
    return () => clearInterval(id);
  }, [sending, streamingText, progressPhoto]);

  // Carrega o total de tokens persistido (controle simples no browser)
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem("aiTokensUsed");
      if (stored != null) {
        const parsed = parseInt(stored, 10);
        if (!Number.isNaN(parsed)) {
          setTotalTokensUsed(parsed);
        } else {
          setTotalTokensUsed(0);
        }
      } else {
        setTotalTokensUsed(0);
      }
    } catch {
      setTotalTokensUsed(0);
    }
  }, []);

  // Busca plano do usuário e define limite de tokens do plano
  useEffect(() => {
    if (!token) return;
    const resolveLimit = plan => {
      if (!plan) return 100000;
      if (typeof plan.ai_feature === "number" && plan.ai_feature > 0) {
        // Se o backend já estiver usando ai_feature como limite absoluto de tokens
        if (plan.ai_feature >= 1000) return plan.ai_feature;
      }
      switch (plan.name) {
        case "Básico":
          return 100_000;
        case "Premium":
          return 1_000_000;
        case "Horizonte":
          return 5_000_000;
        default:
          return 100000;
      }
    };

    const loadPlanFromCacheOrApi = async () => {
      try {
        let plan = null;
        const cached = window.localStorage.getItem("cachedUserPlan");
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (parsed && parsed.name) {
              plan = parsed;
            }
          } catch {
            // ignora cache inválido
          }
        }
        if (!plan) {
          const { data } = await api.get("/plans/user", {
            headers: { Authorization: `Bearer ${token}` }
          });
          plan = data && data[0] ? data[0] : null;
          if (plan) {
            try {
              window.localStorage.setItem("cachedUserPlan", JSON.stringify(plan));
            } catch {
              // ignore
            }
          }
        }
        if (plan) {
          setPlanInfo(plan);
          setPlanTokenLimit(resolveLimit(plan));
        }
      } catch {
        // se falhar, mantém limite padrão
        if (!planTokenLimit) setPlanTokenLimit(100000);
      }
    };

    loadPlanFromCacheOrApi();
  }, [token, planTokenLimit]);

  const fetchGreeting = async () => {
    if (!token || !open) return;
    setGreetingLoading(true);
    setGreeting("");
    try {
      const response = await fetch(`${api.defaults.baseURL}/assistant/photo-analysis/get-greeting-stream`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Erro ao carregar saudação");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let full = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.content) {
                full += data.content;
                setGreeting(full);
              }
            } catch (_) {}
          }
        }
      }
    } catch (err) {
      setGreeting("Olá! Sou o assistente de IA. Qual patologia ou situação você deseja avaliar neste projeto?");
    } finally {
      setGreetingLoading(false);
    }
  };

  const fetchConversations = async () => {
    if (!token || !folderPath) return;
    try {
      const { data } = await api.get("/assistant/chats", {
        params: { context_type: "photo_gallery", context_key: folderPath },
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(data || []);
    } catch (_) {
      setConversations([]);
    }
  };

  const fetchContextFiles = async () => {
    if (!token || !folderPath) return;
    try {
      const { data } = await api.post(
        "/reports/list_files",
        { folder_path: folderPath },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContextFiles(data.files || []);
    } catch (_) {
      setContextFiles([]);
    }
  };

  const buildContextPrompt = useCallback(
    files => {
      if (!files || !files.length) return "";
      const limited = files.slice(0, 50);
      const lines = limited.map(file => {
        const name = file.name || (file.url ? file.url.split("/").pop() : "arquivo");
        return `- ${name}`;
      });
      return [
        `Contexto: você está analisando um relatório na pasta "${folderPath}".`,
        "Estes são alguns dos arquivos disponíveis para análise:",
        ...lines,
        "Considere esses arquivos ao responder às perguntas do usuário."
      ].join("\n");
    },
    [folderPath]
  );

  const renderMarkdown = text => {
    if (!text) return "";
    return (
      text
        // Headers: ###, ##, #
        .replace(/^### (.*)$/gm, "<strong>$1</strong>")
        .replace(/^## (.*)$/gm, "<strong>$1</strong>")
        .replace(/^# (.*)$/gm, "<strong>$1</strong>")
        // Negrito: **texto** ou __texto__
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
        .replace(/__(.*?)__/g, "<strong>$1</strong>")
        // Itálico: *texto* ou _texto_
        .replace(/\*(.*?)\*/g, "<em>$1</em>")
        .replace(/_(.*?)_/g, "<em>$1</em>")
        // Código inline: `código`
        .replace(/`(.*?)`/g, "<code>$1</code>")
        // Quebras de linha
        .replace(/\n/g, "<br>")
    );
  };

  useEffect(() => {
    if (open && token && folderPath) {
      fetchGreeting();
      fetchConversations();
      fetchContextFiles();
    }
  }, [open, token, folderPath]);

  const loadChat = async chatId => {
    if (!token) return;
    try {
      const { data } = await api.get(`/assistant/chats/${chatId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages((data.messages || []).map(m => ({ sender: m.role === "user" ? "user" : "bot", text: m.content })));
      setSelectedChatId(chatId);
      setShowConversations(false);
    } catch (_) {
      setError("Não foi possível carregar a conversa.");
    }
  };

  const handleSend = async () => {
    const baseText = (userMessage || "").trim();
    if (!baseText || !token || !folderPath || sending) return;

    const limit = planTokenLimit || 100000;
    const used = totalTokensUsed || 0;
    if (limit && used >= limit) {
      setShowUpgradeDialog(true);
      return;
    }

    const contextPrompt = buildContextPrompt(contextFiles);
    const composedText = contextPrompt
      ? `${contextPrompt}\n\nPergunta do usuário: ${baseText}`
      : baseText;

    setMessages(prev => [...prev, { sender: "user", text: baseText }]);
    lastAspectoRef.current = baseText;
    setUserMessage("");
    setSending(true);
    setStreamingText("");
    setProgressPhoto(null);
    setError(null);

    try {
      const response = await fetch(`${api.defaults.baseURL}/assistant/photo-analysis/start-chat-stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          message: composedText,
          folder_path: folderPath,
          chat_id: selectedChatId || undefined,
          annotate_images: annotateImages
        })
      });
      if (!response.ok) throw new Error("Erro na análise");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let full = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === "progress" && data.current != null && data.total != null) {
                setProgressPhoto({ current: data.current, total: data.total });
              }
              if (data.content) {
                full += data.content;
                setStreamingText(full);
              }
              if (data.type === "complete") {
                setMessages(prev => [...prev, { sender: "bot", text: full }]);
                setStreamingText("");
                setProgressPhoto(null);
                setSelectedChatId(null);
                if (data.need_approval && data.analyses) {
                  setPendingApproval({ parecer: full, analyses: data.analyses });
                }
                if (data.usage && typeof data.usage.total_tokens === "number") {
                  setTotalTokensUsed(prev => {
                    const base = prev == null ? 0 : prev;
                    const next = base + data.usage.total_tokens;
                    try {
                      window.localStorage.setItem("aiTokensUsed", String(next));
                    } catch {
                      // ignore storage errors
                    }
                    if (planTokenLimit && next >= planTokenLimit) {
                      setShowUpgradeDialog(true);
                    }
                    return next;
                  });
                } else if (totalTokensUsed === null) {
                  setTotalTokensUsed(0);
                }
                fetchConversations();
              }
              if (data.type === "error") {
                setError(data.error || "Erro na análise");
              }
            } catch (_) {}
          }
        }
      }
    } catch (err) {
      setError(err.message || "Erro ao enviar. Tente novamente.");
    } finally {
      setSending(false);
      setStreamingText("");
      setProgressPhoto(null);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setShowConversations(false);
    setMessages([]);
    setStreamingText("");
    setSelectedChatId(null);
    setProgressPhoto(null);
    setError(null);
    setPendingApproval(null);
  };

  const handleApprove = async () => {
    if (!pendingApproval || !token || !folderPath || savingResumo) return;
    setSavingResumo(true);
    setError(null);
    try {
      const { data } = await api.post(
        "/assistant/photo-analysis/save-resumo",
        {
          folder_path: folderPath,
          parecer_content: pendingApproval.parecer,
          aspecto_solicitado: lastAspectoRef.current?.trim() || undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.status === "success") {
        let annotateMsg = "";
        if (annotateImages && pendingApproval.analyses?.length > 0) {
          try {
            const annotRes = await api.post(
              "/assistant/photo-analysis/annotate-images",
              { folder_path: folderPath, analyses: pendingApproval.analyses },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            const d = annotRes.data;
            if (d.saved?.length > 0) {
              annotateMsg = ` ${d.saved.length} imagem(ns) anotada(s) salva(s) em anotadas/.`;
            }
            if (d.failed?.length > 0) {
              annotateMsg += ` (${d.failed.length} falhou: verifique OPENROUTER_API_KEY e modelo Nano Banana Pro)`;
            }
          } catch (annErr) {
            annotateMsg = " Erro ao anotar imagens: " + (annErr.response?.data?.detail || annErr.message);
          }
        }
        setPendingApproval(null);
        setMessages(prev => [...prev, { sender: "bot", text: "Resumo salvo em resumo.json no Azure." + annotateMsg }]);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Erro ao salvar.");
    } finally {
      setSavingResumo(false);
    }
  };

  const handleReject = () => {
    setPendingApproval(null);
  };

  const DraggablePaper = useCallback(
    props => (
      <Paper
        {...props}
        style={{
          ...props.style,
          position: "fixed",
          left: position.x,
          top: position.y,
          margin: 0,
          maxHeight: "calc(100vh - 40px)"
        }}
      />
    ),
    [position.x, position.y]
  );

  if (!visible) return null;

  return (
    <>
      <div className="ai-fab-root">
        <Tooltip title="Vertex IA" placement="left" arrow>
          <Fab
            className="ai-fab-button"
            color="primary"
            aria-label="Vertex IA"
            onClick={handleOpen}
          >
            <i className="fas fa-robot" style={{ fontSize: 24 }} />
          </Fab>
        </Tooltip>
      </div>

      <Dialog
        open={open}
        onClose={handleClose}
        PaperComponent={DraggablePaper}
        PaperProps={{ className: "ai-assistant-paper" }}
        maxWidth={false}
      >
        <div
          className="ai-assistant-header ai-assistant-header-draggable"
          onMouseDown={handleDragStart}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") e.preventDefault();
          }}
          aria-label="Arrastar para mover"
        >
          <div>
            <h2>
              <i className="fas fa-robot" style={{ fontSize: 20, marginRight: 8 }} /> Assistente IA
            </h2>
            <div
              style={{
                fontSize: "0.85rem",
                fontWeight: 400,
                marginTop: 4,
                opacity: 0.9
              }}
            >
              {planTokenLimit
                ? `Tokens consumidos: ${((totalTokensUsed || 0) / 1000).toFixed(1)}k de ${(planTokenLimit / 1000).toFixed(0)}k (plano ${
                    planInfo?.name || "atual"
                  })`
                : `Tokens consumidos: ${((totalTokensUsed || 0) / 1000).toFixed(1)}k`}
            </div>
          </div>
          <IconButton
            size="small"
            onClick={handleClose}
            onMouseDown={e => e.stopPropagation()}
            style={{ color: "#fff" }}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <DialogContent className="ai-assistant-content">
          <div className="ai-conversations-drawer">
            <Button
              startIcon={<HistoryIcon />}
              onClick={() => setShowConversations(!showConversations)}
              size="small"
              color="primary"
            >
              {showConversations ? "Ocultar" : "Ver conversas anteriores"}
            </Button>
            {showConversations && (
              <div className="ai-conversations-list">
                {conversations.length === 0 ? (
                  <p style={{ padding: 8, color: "#888", fontSize: 14 }}>Nenhuma conversa anterior.</p>
                ) : (
                  conversations.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      className="ai-conversation-item"
                      onClick={() => loadChat(c.id)}
                    >
                      {c.title || `Conversa ${c.id}`}
                      <small>
                        {c.updated_at ? new Date(c.updated_at).toLocaleString("pt-BR") : ""}
                      </small>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {messages.length === 0 && !streamingText && (
            <div className="ai-assistant-welcome">
              {greetingLoading ? (
                <CircularProgress size={24} />
              ) : (
                <span
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdown(greeting || "Carregando...")
                  }}
                />
              )}
            </div>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              className={`ai-message ${msg.sender === "user" ? "ai-message-user" : "ai-message-assistant"}`}
              dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.text) }}
            />
          ))}

          {streamingText && (
            <div className="ai-message ai-message-assistant">
              <span
                dangerouslySetInnerHTML={{
                  __html: renderMarkdown(streamingText)
                }}
              />
              <span className="ai-streaming-cursor" />
            </div>
          )}

          {error && (
            <div style={{ color: "#c62828", fontSize: 14, padding: 8 }}>{error}</div>
          )}

          {sending && !streamingText && (
            <div className="ai-thinking-message">
              {progressPhoto && progressPhoto.total > 0 ? (
                <span>
                  {progressPhoto.current}/{progressPhoto.total} fotos sendo analisada{progressPhoto.current === 1 ? "" : "s"}…
                </span>
              ) : (
                <span className="ai-thinking-dots">{thinkingText}</span>
              )}
            </div>
          )}

          <div ref={contentEndRef} />

          {totalTokensUsed !== null && (
            <div className="ai-tokens-info" title="Total de tokens utilizados nesta sessão">
              {planTokenLimit
                ? `Tokens consumidos: ${(totalTokensUsed / 1000).toFixed(1)}k de ${(planTokenLimit / 1000).toFixed(0)}k`
                : `Tokens consumidos: ${(totalTokensUsed / 1000).toFixed(1)}k`}
            </div>
          )}
        </DialogContent>

        {pendingApproval && (
          <div className="ai-assistant-approval-area" style={{ padding: "8px 16px", borderTop: "1px solid #eee", display: "flex", gap: 8, alignItems: "center" }}>
            <Button size="small" variant="contained" color="primary" onClick={handleApprove} disabled={savingResumo}>
              {savingResumo ? "Salvando…" : "Aprovar e salvar"}
            </Button>
            <Button size="small" variant="outlined" color="default" onClick={handleReject} disabled={savingResumo}>
              Rejeitar
            </Button>
          </div>
        )}

        <div className="ai-assistant-input-area">
          {!pendingApproval && (
            <FormControlLabel
              control={<Checkbox checked={annotateImages} onChange={e => setAnnotateImages(e.target.checked)} color="primary" size="small" />}
              label={<span style={{ fontSize: "0.75rem" }}>Marcar na imagem o que a IA encontrar (OpenRouter / Nano Banana Pro)</span>}
              style={{ marginBottom: 4, marginLeft: 8 }}
            />
          )}
          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              placeholder="Digite sua mensagem..."
              value={userMessage}
              onChange={e => setUserMessage(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              variant="outlined"
              size="small"
              disabled={sending || !!pendingApproval || (planTokenLimit && (totalTokensUsed || 0) >= planTokenLimit)}
              InputProps={{ style: { borderRadius: 20 } }}
            />
            <Button
              className={classes.sendButton}
              variant="contained"
              color="primary"
              disabled={
                !userMessage.trim() ||
                sending ||
                !!pendingApproval ||
                (planTokenLimit && (totalTokensUsed || 0) >= planTokenLimit)
              }
              onClick={handleSend}
            >
              Enviar
            </Button>
          </div>
        </div>

        {showUpgradeDialog && (
          <Dialog
            open={showUpgradeDialog}
            onClose={() => setShowUpgradeDialog(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogContent style={{ padding: "24px 20px" }}>
              <h3 style={{ marginTop: 0, marginBottom: 8 }}>Limite de tokens atingido</h3>
              <p style={{ marginBottom: 16, fontSize: 14 }}>
                Você atingiu o <strong>Total de Tokens</strong> do plano{" "}
                <strong>{planInfo?.name || "atual"}</strong> para o Assistente de IA.
              </p>
              <p style={{ marginBottom: 24, fontSize: 14 }}>
                Para continuar usando a IA sem interrupções, faça upgrade para um plano superior.
              </p>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <Button
                  onClick={() => setShowUpgradeDialog(false)}
                  size="small"
                  variant="outlined"
                  color="default"
                >
                  Fechar
                </Button>
                <Button
                  onClick={() => {
                    setShowUpgradeDialog(false);
                    navigate("/plans");
                  }}
                  size="small"
                  variant="contained"
                  color="primary"
                >
                  Ver Planos
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </Dialog>
    </>
  );
};

export default AIAssistantFab;
